import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

type AuthView = 'signIn' | 'signUp' | 'forgotPassword' | 'verificationPending';

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

export const AuthGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    user, loading, isRecoveryMode,
    signInWithGoogle, signInWithEmail, signUpWithEmail,
    resetPassword, updatePassword,
  } = useAuth();

  const hasInvite = new URLSearchParams(window.location.search).has('invite');

  const [view, setView] = useState<AuthView>(hasInvite ? 'signUp' : 'signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [inviteData, setInviteData] = useState<{ email: string; orgName: string } | null>(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const switchView = (newView: AuthView) => {
    setError('');
    setView(newView);
  };

  // Peek invite on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('invite');
    if (!token) return;

    setInviteLoading(true);
    fetch(`/api/invites/peek?token=${encodeURIComponent(token)}`)
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) {
          setInviteData(data);
          setEmail(data.email);
        }
      })
      .catch(() => {}) // Silently fail — user can still type their email
      .finally(() => setInviteLoading(false));
  }, []);

  // 1. Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-slate-400 text-lg">Loading...</div>
      </div>
    );
  }

  // 2. Recovery mode — show reset form even though user is truthy
  if (user && isRecoveryMode) {
    return <ResetPasswordView
      password={password}
      setPassword={setPassword}
      confirmPassword={confirmPassword}
      setConfirmPassword={setConfirmPassword}
      error={error}
      setError={setError}
      submitting={submitting}
      setSubmitting={setSubmitting}
      updatePassword={updatePassword}
    />;
  }

  // 3. Authenticated — pass through
  if (user) return <>{children}</>;

  // 4. Not authenticated — show auth forms
  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const result = await signInWithEmail(email, password);
      if (result.error) {
        if (result.error.includes('Email not confirmed')) {
          setError('Please verify your email before signing in. Check your inbox for a verification link.');
        } else {
          setError(result.error);
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) { setError('Email is required'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }

    setSubmitting(true);
    try {
      const result = await signUpWithEmail(email, password);
      if (result.error) {
        setError(result.error);
      } else if (result.needsVerification) {
        switchView('verificationPending');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const result = await resetPassword(email);
      if (result.error) {
        setError(result.error);
      } else {
        setResetSent(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const subtitle = inviteData
    ? `Sign in to accept your invitation to ${inviteData.orgName}.`
    : 'Sign in to access your workspaces, personas, and evaluations.';

  if (view === 'verificationPending') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-10 max-w-md w-full text-center shadow-2xl">
          <svg className="w-16 h-16 mx-auto mb-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
          </svg>
          <h2 className="text-2xl font-bold text-white mb-2">Check Your Email</h2>
          <p className="text-slate-400 mb-6 text-sm">
            We've sent a verification link to <span className="font-semibold text-white">{email}</span>. Click the link in your email to verify your account, then come back and sign in.
          </p>
          <button
            onClick={() => switchView('signIn')}
            className="text-sm text-slate-400 hover:text-white transition-colors cursor-pointer"
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  if (view === 'forgotPassword') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-10 max-w-md w-full text-center shadow-2xl">
          <h2 className="text-2xl font-bold text-white mb-2">Reset Password</h2>
          <p className="text-slate-400 mb-8 text-sm">
            Enter your email address and we'll send you a link to reset your password.
          </p>
          {resetSent ? (
            <div className="mb-6">
              <p className="text-green-400 text-sm mb-4">Check your email for a password reset link.</p>
              <button
                onClick={() => { setResetSent(false); switchView('signIn'); }}
                className="text-sm text-slate-400 hover:text-white transition-colors cursor-pointer"
              >
                Back to sign in
              </button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="w-full px-6 py-3 bg-brand-primary text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Sending...' : 'Send Reset Link'}
              </button>
              <button
                type="button"
                onClick={() => switchView('signIn')}
                className="text-sm text-slate-400 hover:text-white transition-colors cursor-pointer"
              >
                Back to sign in
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  if (view === 'signUp') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-10 max-w-md w-full text-center shadow-2xl">
          <h1 className="text-3xl font-bold text-white mb-2">Create Account</h1>
          <p className="text-slate-400 mb-8 text-sm">
            {inviteData
              ? `You've been invited to join ${inviteData.orgName}. Create your password to get started.`
              : 'Create your account to get started.'}
          </p>
          <button
            onClick={signInWithGoogle}
            className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-white text-slate-800 font-semibold rounded-xl hover:bg-slate-100 transition-colors shadow-lg"
          >
            <GoogleIcon />
            Sign up with Google
          </button>
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-slate-600" />
            <span className="text-sm text-slate-500">or</span>
            <div className="flex-1 h-px bg-slate-600" />
          </div>
          <form onSubmit={handleSignUp} className="space-y-4 text-left">
            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                readOnly={!!inviteData}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <p className="text-slate-500 text-xs mt-1">Must be at least 8 characters</p>
            </div>
            <div>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm password"
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            {error && <p className="text-red-400 text-sm">{error}</p>}
            <button
              type="submit"
              disabled={submitting}
              className="w-full px-6 py-3 bg-brand-primary text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>
          <p className="mt-6 text-sm text-slate-400">
            Already have an account?{' '}
            <button
              onClick={() => switchView('signIn')}
              className="text-slate-400 hover:text-white transition-colors cursor-pointer underline"
            >
              Sign in
            </button>
          </p>
        </div>
      </div>
    );
  }

  // Default: signIn view
  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-10 max-w-md w-full text-center shadow-2xl">
        <h1 className="text-3xl font-bold text-white mb-2">Persona Builder</h1>
        <p className="text-slate-400 mb-8 text-sm">{subtitle}</p>
        {inviteLoading && (
          <p className="text-slate-500 text-xs mb-4">Loading invitation details...</p>
        )}
        <button
          onClick={signInWithGoogle}
          className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-white text-slate-800 font-semibold rounded-xl hover:bg-slate-100 transition-colors shadow-lg"
        >
          <GoogleIcon />
          Sign in with Google
        </button>
        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-slate-600" />
          <span className="text-sm text-slate-500">or</span>
          <div className="flex-1 h-px bg-slate-600" />
        </div>
        <form onSubmit={handleSignIn} className="space-y-4 text-left">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address"
            readOnly={!!inviteData}
            className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <button
            type="submit"
            disabled={submitting}
            className="w-full px-6 py-3 bg-brand-primary text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        <div className="mt-4 text-right">
          <button
            onClick={() => switchView('forgotPassword')}
            className="text-sm text-slate-400 hover:text-white transition-colors cursor-pointer"
          >
            Forgot password?
          </button>
        </div>
        {hasInvite && (
          <p className="mt-4 text-sm text-slate-400">
            Don't have an account?{' '}
            <button
              onClick={() => switchView('signUp')}
              className="text-slate-400 hover:text-white transition-colors cursor-pointer underline"
            >
              Sign up
            </button>
          </p>
        )}
      </div>
    </div>
  );
};

// Reset Password sub-view (shown during recovery mode)
const ResetPasswordView: React.FC<{
  password: string;
  setPassword: (v: string) => void;
  confirmPassword: string;
  setConfirmPassword: (v: string) => void;
  error: string;
  setError: (v: string) => void;
  submitting: boolean;
  setSubmitting: (v: boolean) => void;
  updatePassword: (pw: string) => Promise<{ error?: string }>;
}> = ({ password, setPassword, confirmPassword, setConfirmPassword, error, setError, submitting, setSubmitting, updatePassword }) => {
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }

    setSubmitting(true);
    try {
      const result = await updatePassword(password);
      if (result.error) {
        setError(result.error);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-10 max-w-md w-full text-center shadow-2xl">
        <h2 className="text-2xl font-bold text-white mb-2">Set New Password</h2>
        <p className="text-slate-400 mb-8 text-sm">Enter your new password below.</p>
        <form onSubmit={handleSubmit} className="space-y-4 text-left">
          <div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="New password"
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            <p className="text-slate-500 text-xs mt-1">Must be at least 8 characters</p>
          </div>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
            className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <button
            type="submit"
            disabled={submitting}
            className="w-full px-6 py-3 bg-brand-primary text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
};
